'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';
import AppImage from '../ui/AppImage';

interface NavigationSection {
  label: string;
  anchor: string;
  description: string;
  priority: number;
}

interface SectionNavigatorProps {
  isCollapsed?: boolean;
  isOpen?: boolean;
}

const SectionNavigator = ({ isCollapsed = false, isOpen = false }: SectionNavigatorProps) => {
  const [activeSection, setActiveSection] = useState('about');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(isOpen);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const sections: NavigationSection[] = [
    {
      label: 'About',
      anchor: 'about',
      description: 'Personal story and credentials',
      priority: 1
    },
    {
      label: 'Services',
      anchor: 'services',
      description: 'Coaching programs and offerings',
      priority: 2
    },
    {
      label: 'Success Stories',
      anchor: 'success-stories',
      description: 'Client testimonials and results',
      priority: 3
    },
    {
      label: 'Portfolio',
      anchor: 'portfolio',
      description: 'Work samples and projects',
      priority: 4
    },
    {
      label: 'FAQ',
      anchor: 'faq',
      description: 'Common questions answered',
      priority: 5
    },
    {
      label: 'Contact',
      anchor: 'contact',
      description: 'Book consultation',
      priority: 6
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sectionElements = sections.map(section => ({
        id: section.anchor,
        element: document.getElementById(section.anchor),
      }));

      const currentSection = sectionElements.find(({ element }) => {
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        return rect.top <= 100 && rect.bottom >= 100;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Block body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (anchor: string) => {
    const element = document.getElementById(anchor);
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
    closeMobileMenu();
  };

  const closeMobileMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsMobileMenuOpen(false);
      setIsClosing(false);
    }, 250);
  };

  const toggleMobileMenu = () => {
    if (isMobileMenuOpen) {
      closeMobileMenu();
    } else {
      setIsClosing(false);
      setIsMobileMenuOpen(true);
    }
  };

  return (
    <>
      <nav data-component-id="src/components/common/SectionNavigator.tsx:134:6" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="134" data-component-file="SectionNavigator.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%7D"
        className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-250 ${isScrolled
          ? 'bg-white/95 backdrop-blur-sm shadow-card border-b border-border'
          : 'bg-white h-18 lg:h-20'
          }`}
      >
        <div data-component-id="src/components/common/SectionNavigator.tsx:140:8" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="140" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8">
          <div data-component-id="src/components/common/SectionNavigator.tsx:141:10" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="141" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20h-20%22%7D" className="flex items-center justify-between h-20">
            {/* Logo */}
            <div data-component-id="src/components/common/SectionNavigator.tsx:143:12" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="143" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%22%7D" className="flex-shrink-0">
              <div data-component-id="src/components/common/SectionNavigator.tsx:144:14" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="144" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-20%20h-20%20rounded-lg%20flex%20items-center%20justify-center%22%7D" className="w-20 h-20 rounded-lg flex items-center justify-center">
                <AppImage data-component-id="src/components/common/SectionNavigator.tsx:145:16" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="145" data-component-file="SectionNavigator.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%2C%22src%22%3A%22%2Fassets%2Fimages%2FLogo.png%22%2C%22alt%22%3A%22Ali%20Fiverr%20Coach%20Logo%22%2C%22className%22%3A%22h-16%20w-16%20lg%3Ah-20%20lg%3Aw-20%22%7D"
                  src="/assets/images/Logo.png"
                  alt="Ali Fiverr Coach Logo"
                  className='h-16 w-16 lg:h-20 lg:w-20'
                  onClick={() => scrollToSection('home')}
                />
              </div>
            </div>

            {/* Desktop Navigation */}
            <div data-component-id="src/components/common/SectionNavigator.tsx:155:12" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="155" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20items-center%20space-x-8%22%7D" className="hidden lg:flex items-center space-x-8">
              {sections.map((section) => (
                <button data-component-id="src/components/common/SectionNavigator.tsx:157:16" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="157" data-component-file="SectionNavigator.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                  key={section.anchor}
                  onClick={() => scrollToSection(section.anchor)}
                  className={`relative px-3 py-2 text-sm font-medium transition-colors duration-250 hover:text-primary ${activeSection === section.anchor
                    ? 'text-primary' : 'text-text-secondary'
                    }`}
                  title={section.description}
                >
                  {section.label}
                  {activeSection === section.anchor && (
                    <span data-component-id="src/components/common/SectionNavigator.tsx:167:20" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="167" data-component-file="SectionNavigator.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20bottom-0%20left-0%20right-0%20h-0.5%20bg-primary%20rounded-full%22%7D" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                </button>
              ))}
            </div>

            {/* CTA Button */}
            <div data-component-id="src/components/common/SectionNavigator.tsx:174:12" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="174" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20items-center%20space-x-4%22%7D" className="hidden lg:flex items-center space-x-4">
              <button data-component-id="src/components/common/SectionNavigator.tsx:175:14" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="175" data-component-file="SectionNavigator.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22bg-accent%20hover%3Abg-accent%2F90%20text-accent-foreground%20px-6%20py-3%20%20font-semibold%20shadow-cta%20transition-all%20rounded-xl%20duration-250%20hover%3Ashadow-lg%20hover%3Ascale-105%22%2C%22textContent%22%3A%22Book%20Consultation%22%7D"
                onClick={() => scrollToSection('contact')}
                className="bg-accent hover:bg-accent/90 text-accent-foreground px-6 py-3  font-semibold shadow-cta transition-all rounded-xl duration-250 hover:shadow-lg hover:scale-105"
              >
                Book Consultation
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div data-component-id="src/components/common/SectionNavigator.tsx:184:12" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="184" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Ahidden%22%7D" className="lg:hidden">
              <button data-component-id="src/components/common/SectionNavigator.tsx:185:14" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="185" data-component-file="SectionNavigator.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22p-2%20%20rounded-lg%20text-text-secondary%20hover%3Atext-primary%20hover%3Abg-muted%20transition-colors%20duration-250%20z-200%20cursor-pointer%22%7D"
                onClick={toggleMobileMenu}
                className="p-2  rounded-lg text-text-secondary hover:text-primary hover:bg-muted transition-colors duration-250 z-200 cursor-pointer"
                aria-label="Toggle navigation menu"
              >
                <Icon data-component-id="src/components/common/SectionNavigator.tsx:190:16" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="190" data-component-file="SectionNavigator.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D"
                  name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                  size={24}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div data-component-id="src/components/common/SectionNavigator.tsx:201:10" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="201" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`lg:hidden fixed inset-0 top-18 z-200 ${isClosing ? 'animate-overlay-out' : 'animate-overlay-in'}`}>
            <div data-component-id="src/components/common/SectionNavigator.tsx:202:12" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="202" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`px-6 py-8 space-y-6 overflow-y-auto bg-white max-h-[calc(100vh-80px)] ${isClosing ? 'animate-slide-out' : 'animate-slide-in'}`}>
              {sections.map((section) => (
                <button data-component-id="src/components/common/SectionNavigator.tsx:204:16" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="204" data-component-file="SectionNavigator.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                  key={section.anchor}
                  onClick={() => scrollToSection(section.anchor)}
                  className={`block w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors duration-250 ${activeSection === section.anchor
                    ? 'text-primary bg-primary/5' : 'text-text-secondary hover:text-primary hover:bg-muted'
                    }`}
                >
                  <div data-component-id="src/components/common/SectionNavigator.tsx:211:18" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="211" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
                    <span data-component-id="src/components/common/SectionNavigator.tsx:212:20" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="212" data-component-file="SectionNavigator.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{section.label}</span>
                    {activeSection === section.anchor && (
                      <Icon data-component-id="src/components/common/SectionNavigator.tsx:214:22" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="214" data-component-file="SectionNavigator.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%2C%22className%22%3A%22text-primary%22%7D" name="CheckIcon" size={20} className="text-primary" />
                    )}
                  </div>
                  <p data-component-id="src/components/common/SectionNavigator.tsx:217:18" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="217" data-component-file="SectionNavigator.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-text-secondary%20mt-1%22%7D" className="text-sm text-text-secondary mt-1">{section.description}</p>
                </button>
              ))}

              <div data-component-id="src/components/common/SectionNavigator.tsx:221:14" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="221" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-6%20border-t%20border-border%22%7D" className="pt-6 border-t border-border">
                <button data-component-id="src/components/common/SectionNavigator.tsx:222:16" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="222" data-component-file="SectionNavigator.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20bg-accent%20hover%3Abg-accent%2F90%20text-accent-foreground%20px-6%20py-4%20rounded-xl%20font-semibold%20shadow-cta%20transition-all%20duration-250%22%2C%22textContent%22%3A%22Book%20Consultation%22%7D"
                  onClick={() => scrollToSection('contact')}
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground px-6 py-4 rounded-xl font-semibold shadow-cta transition-all duration-250"
                >
                  Book Consultation
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Spacer to prevent content overlap */}
      <div data-component-id="src/components/common/SectionNavigator.tsx:235:6" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="235" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-16%20lg%3Ah-20%22%7D" className="h-16 lg:h-20" />
    </>
  );
};

export default SectionNavigator;